/**
 * WebView Authentication Service
 * Handles authentication flow for WebView integration
 */

import { getAuth, signInWithCustomToken } from 'firebase/auth';
import app from '../../../lib/firebase';
import { FirebaseTokenService } from './firebaseTokenService';
import { WebViewAuthConfig } from '../types';
import { useAuthStore } from '../../../store/useAuthStore';
import { usePersistedAuthStore } from '../../../store/usePersistedAuthStore';
import { useUserStore } from '../../../store/useUserStore';

export class WebViewAuthService {
  private static instance: WebViewAuthService;
  private firebaseTokenService: FirebaseTokenService;
  private auth = getAuth(app);

  private constructor() {
    this.firebaseTokenService = FirebaseTokenService.getInstance();
  }

  static getInstance(): WebViewAuthService {
    if (!WebViewAuthService.instance) {
      WebViewAuthService.instance = new WebViewAuthService();
    }
    return WebViewAuthService.instance;
  }

  /**
   * Authenticate user with WebView credentials
   */
  async authenticate(authConfig: WebViewAuthConfig): Promise<void> {
    try {
      // 1. WebView'de tenant null olmalı - tenant ayarlamıyoruz

      // 2. Get Firebase token
      const firebaseToken = await this.firebaseTokenService.exchangeToken(
        authConfig.firebaseAuthUrl,
        authConfig.accessToken,
        String(authConfig.tenantId)
      );

      // 3. Sign in to Firebase
      await signInWithCustomToken(this.auth, firebaseToken);

      // 4. Set user authentication state
      this.setAuthState(authConfig);

      console.log('WebView authentication successful');
    } catch (error) {
      console.error('WebView authentication failed:', error);
      throw error;
    }
  }


  /**
   * Set authentication state in stores
   */
  private setAuthState(authConfig: WebViewAuthConfig): void {
    const { setAccessToken } = useAuthStore.getState();
    const { setRefreshToken, setUserId } = usePersistedAuthStore.getState();
    const { setUserInfo } = useUserStore.getState();

    // Set access token (default 24 hours expiry)
    setAccessToken(authConfig.accessToken, 86400);

    // Set refresh token if provided
    if (authConfig.refreshToken) {
      setRefreshToken(authConfig.refreshToken);
    }

    // Set user ID - keep as string if it's a UUID
    const userId = authConfig.userInfo.id;
    setUserId(userId);

    // Map and set user info
    const mappedUserInfo = this.mapUserInfo(authConfig.userInfo);
    setUserInfo(mappedUserInfo);
  }

  /**
   * Map WebView user info to internal format
   */
  private mapUserInfo(userInfo: WebViewAuthConfig['userInfo']) {
    // Handle both 'role' and 'roles' formats
    const role = userInfo.role || (userInfo.roles && userInfo.roles[0]);
    const roleNames = this.mapRoleToRoleNames(role || 'student');
    const [firstName, ...lastNameParts] = userInfo.name.split(' ');
    const lastName = lastNameParts.join(' ');

    return {
      id: userInfo.id as any, // Keep the original type (number or string)
      userName: userInfo.email || userInfo.name,
      name: firstName || userInfo.name,
      surname: lastName || '',
      emailAddress: userInfo.email,
      isActive: true,
      fullName: userInfo.name,
      lastLoginTime: null,
      creationTime: new Date(),
      roleNames,
    };
  }

  /**
   * Map WebView roles to internal role names
   */
  private mapRoleToRoleNames(role: string): string[] {
    switch (role) {
      case 'teacher':
        return ['Teacher'];
      case 'admin':
        return ['Admin', 'Teacher'];
      case 'student':
        return ['Student'];
      case 'viewer':
        return ['Viewer'];
      default:
        return ['Student'];
    }
  }

  /**
   * Check if user is authenticated via WebView
   */
  isAuthenticated(): boolean {
    const { accessToken, isTokenExpired } = useAuthStore.getState();
    const { userInfo } = useUserStore.getState();
    const firebaseUser = this.auth.currentUser;

    return !!(
      accessToken && 
      !isTokenExpired() && 
      userInfo && 
      firebaseUser &&
      !this.firebaseTokenService.isTokenExpired()
    );
  }

  /**
   * Logout and clear all auth state
   */
  async logout(): Promise<void> {
    try {
      // Sign out from Firebase
      await this.auth.signOut();

      // Clear Firebase token
      this.firebaseTokenService.clearToken();

      // Clear auth stores
      useAuthStore.getState().clearAuth();
      usePersistedAuthStore.getState().clearPersistedAuth();
      useUserStore.getState().clearUserInfo();

      console.log('WebView logout successful');
    } catch (error) {
      console.error('WebView logout error:', error);
      throw error;
    }
  }
}